import { ComponentProps } from 'react';
import { Controller } from 'react-hook-form';

import { SwitchProps } from '@mui/material/Switch';

import { styled, Switch as BaseSwitch, theme } from '@sbv-clients/ui-shared';

export type SwitchSizeVariant = 'regular' | 'small';

export type FormToggleFieldProps = Omit<SwitchProps, 'size'> & {
  name: string;
  variant?: SwitchSizeVariant;
};

export type RenderType = ComponentProps<typeof Controller>['render'];

const sizeStyles = {
  regular: {
    width: 40,
    height: 24,
    thumbSize: 20,
    checkedTransform: 16,
  },
  small: {
    width: 28,
    height: 16,
    thumbSize: 12,
    checkedTransform: 12,
  },
};

export const StyledSwitch = styled(BaseSwitch, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: SwitchSizeVariant }>(({ theme, variant }) => {
  const { width, height, thumbSize, checkedTransform } = sizeStyles[variant];

  return {
    width,
    height,
    padding: 0,
    display: 'flex',

    '& .MuiSwitch-switchBase': {
      padding: (height - thumbSize) / 2,
      '&.Mui-checked': {
        transform: `translateX(${checkedTransform}px)`,
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.success.main,
          opacity: 1,
        },
        '& .MuiSwitch-thumb': {
          backgroundColor: theme.background?.white,
          boxShadow: theme.colors.shadows.switchThumb,
          width: thumbSize,
          height: thumbSize,
        },
      },
      '&.Mui-disabled': {
        '& + .MuiSwitch-track': {
          opacity: 0.12,
        },
      },
    },

    '& .MuiSwitch-thumb': {
      backgroundColor: theme.background?.white,
      boxShadow: theme.colors.shadows.switchThumb,
      width: thumbSize,
      height: thumbSize,
      borderRadius: thumbSize / 2,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
      border: 'none',
    },

    '& .MuiSwitch-track': {
      borderRadius: height / 2,
      backgroundColor: theme.background?.switchNegative,
      opacity: 1,
      boxSizing: 'border-box',
      border: 'none',
    },
  };
});
